import React from 'react';
import styles from './gameRules.module.css';
import RuleIcon from '../../svgs/RuleIcon';
import { Card, CardContent, CardHeader } from '@mui/material';
import { Rule } from '../../types/Rule'
import { ChallengeMode } from '../../types/enums';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';

export interface RuleProps{
    rule: string;
    afterText?: string;
    icon?: JSX.Element;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    value?: string | number | readonly string[];
    displayOnly?: boolean;
    isSubRule?: boolean;
}
const RuleDisplay: React.FC<RuleProps> = ({ 
    rule, afterText, icon = <RuleIcon/>, value,
    onChange, displayOnly, isSubRule
})=>{

    // Refs
    let spanRef = React.useRef<any>();

    // Classes
    const mainClass = isSubRule ? `${styles.ruleMain} ${styles.subRule}` : styles.ruleMain

    return(
        <div className={mainClass}>
            {/** Icon */}
            <div className={styles.icon}>
                {icon}
            </div>

            {/** Label */}
            <div className={styles.labelGroup}>
                <p>{rule}
                    <span ref={spanRef}>
                        {
                            displayOnly ? 
                            value: 
                            <input type="text" onChange={onChange} value={value}/>
                        }
                    </span>
                    {afterText}
                </p>
            </div>
        </div>
    )
}

export interface GameRuleState{
    maxSkip?: string | number;
    maxTime?: string | number;
    plusPoints?: string | number;
    minusPoints?: string | number;
    roundName?: string | number;
}

export interface GameRulesProps{
    rule?: Rule;
    mode?: ChallengeMode
    displayOnly?: boolean;
    onRuleChange?: (state: GameRuleState)=> any;
}
const GameRules: React.FC<GameRulesProps> = ({ 
    rule, onRuleChange, displayOnly, mode = ChallengeMode.FIXED_TIME 
})=>{

    // State
    const [maxSkip, setMaxSkip] = React.useState<string | number>('');
    const [maxTime, setMaxTime] = React.useState<string | number>('');
    const [plusPoints, setPlusPoints] = React.useState<string | number>('');
    const [minusPoints, setMinusPoints] = React.useState<string | number>('');
    const [roundName, setRoundName] = React.useState<string | number>('');

    // Handlers
    const handleMaxSkipChange = (event: React.ChangeEvent<HTMLInputElement>)=>{
        onRuleChange && onRuleChange({ 
            maxSkip: event.currentTarget.value, maxTime, plusPoints, minusPoints, roundName 
        })
        setMaxSkip(event.currentTarget.value);
    }
    const handleMaxTimeChange = (event: React.ChangeEvent<HTMLInputElement>)=>{
        onRuleChange && onRuleChange({ 
            maxTime: event.currentTarget.value, maxSkip, plusPoints, minusPoints, roundName 
        })
        setMaxTime(event.currentTarget.value);
    }
    const handlePlusPointsChange = (event: React.ChangeEvent<HTMLInputElement>)=>{
        onRuleChange && onRuleChange({ 
            plusPoints: event.currentTarget.value, maxSkip, maxTime, minusPoints, roundName 
        })
        setPlusPoints(event.currentTarget.value);
    }
    const handleMinusPointsChange = (event: React.ChangeEvent<HTMLInputElement>)=>{
        onRuleChange && onRuleChange({ 
            minusPoints: event.currentTarget.value, maxSkip, maxTime, plusPoints, roundName 
        })
        setMinusPoints(event.currentTarget.value);
    }
    const handleRoundNameChange = (event: React.ChangeEvent<HTMLInputElement>)=>{
        onRuleChange && onRuleChange({ 
            roundName: event.currentTarget.value, maxSkip, maxTime, plusPoints, minusPoints 
        })
        setRoundName(event.currentTarget.value);
    }

    // Effect
    React.useEffect(()=>{
        if (rule) {
            setMaxSkip(rule.maxSkip || 0);   
            setMaxTime(rule.maxTime || 0);   
            setPlusPoints(rule.plusPoints || 0);   
            setMinusPoints(rule.minusPoints || 0);   
            setRoundName(rule.roundName || 0);   
        }
    }, [rule])

    // Rules
    const rules: RuleProps[] =[
        { rule: "You can only skip ", afterText: "X", value: maxSkip, onChange: handleMaxSkipChange },
        { rule: "Time duration is ", afterText: "s", value: maxTime, onChange: handleMaxTimeChange },
        { 
            rule: "Each right answer has ", afterText: "points addition", 
            value: plusPoints, onChange: handlePlusPointsChange 
        },
        { 
            rule: "Each wrong answer has ", afterText: "points reduction",
            value: minusPoints, onChange: handleMinusPointsChange 
        },
    ]

    return(
        <Card sx={{ backgroundColor: '#ff8503', borderRadius: '23px', maxWidth: "800px", overflow: "unset" }}>
            <CardHeader title="Game Rules"
                sx={{
                    fontFamily: 'Reddit Sans Variable',
                    fontStyle: 'italic',
                    lineHeight: '43px',
                    color: 'white',
                    textAlign: 'center',
                    '& .MuiCardHeader-title	':{
                        fontWeight: '500',
                        fontSize: '30px',
                    }
                }}
            />
            <CardContent>
                {
                    rules.map((rule, index)=>{

                        if (mode === ChallengeMode.SUDDEN_DEATH && (rule.rule.includes("skip") || rule.afterText?.includes("reduction"))) {
                            
                        }
                        else{
                            return (
                                <RuleDisplay key={`rule${index}`} {...rule} displayOnly={displayOnly} />
                            )
                        }
                    })
                }
                {
                    mode === ChallengeMode.SUDDEN_DEATH &&
                   <>
                        <RuleDisplay
                            displayOnly={displayOnly}
                            rule="Answer incorrectly and the performance ENDS!"
                        />
                        <RuleDisplay
                            displayOnly={displayOnly}
                            rule="Your score depends on the quarter the performance ends:"
                        />
                        <RuleDisplay
                            displayOnly={displayOnly}
                            isSubRule
                            icon={<HorizontalRuleIcon sx={{ color: 'white' }}/>}
                            rule="0% of score if within the first quarter"
                        />
                        <RuleDisplay
                            displayOnly={displayOnly}
                            isSubRule
                            icon={<HorizontalRuleIcon sx={{ color: 'white' }}/>}
                            rule="50% of score if within the second quarter"
                        />
                        <RuleDisplay
                            displayOnly={displayOnly}
                            isSubRule
                            icon={<HorizontalRuleIcon sx={{ color: 'white' }}/>}
                            rule="100% of score if within the third quarter"
                        />
                        <RuleDisplay
                            displayOnly={displayOnly}
                            isSubRule
                            icon={<HorizontalRuleIcon sx={{ color: 'white' }}/>}
                            rule="150% of score if within the last quarter "
                        />
                        
                   </>
                }
                 <div className={styles.ruleMain}>
                    {/** Icon */}
                    <div className={styles.icon}>
                        <RuleIcon/>
                    </div>

                    {/** Label */}
                    <div className={styles.labelGroup}>
                        <p>
                            <span>
                                {
                                    displayOnly ? 
                                    roundName :
                                    <input type="text" style={{ width: "70%", textAlign: 'left' }}
                                        value={roundName} onChange={handleRoundNameChange}
                                    />
                                }
                            </span>
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

export default GameRules;